import type { LearnedAutoFillPatternV1 } from '../../../shared/lib/quickFill/learnedAutoFillLibrary';
import {
  createEmptyQuickFillKeyLibraryEntry,
  updateQuickFillKeyLibraryEntry,
  type QuickFillKeyLibraryEditableField,
} from './quickFillKeyLibraryDrafts';
import type { QuickFillKeyLibraryDialogSource } from './quickFillKeyLibraryTableModel';

export type QuickFillKeyLibraryGuardIntent =
  | { type: 'close' }
  | { type: 'import'; file: File }
  | { type: 'delete-selected' };

export interface QuickFillKeyLibraryEditorState {
  open: boolean;
  source: QuickFillKeyLibraryDialogSource;
  persistedEntries: LearnedAutoFillPatternV1[];
  libraryEntries: LearnedAutoFillPatternV1[];
  drafts: LearnedAutoFillPatternV1[];
  selectedLocalEntryIndexes: Set<number>;
  confirmedConflictIds: Set<string>;
  skippedRowCount: number;
  duplicateRowCount: number;
  error?: string;
  notice?: string;
  guardIntent?: QuickFillKeyLibraryGuardIntent;
}

type ChangeEntryAction = {
  type: 'change-library-entry' | 'change-draft-entry';
  index: number;
  field: QuickFillKeyLibraryEditableField;
  value: string;
};

export type QuickFillKeyLibraryEditorAction =
  | { type: 'open-manage'; entries: readonly LearnedAutoFillPatternV1[] }
  | {
    type: 'open-preview';
    entries: readonly LearnedAutoFillPatternV1[];
    drafts: readonly LearnedAutoFillPatternV1[];
    source: Exclude<QuickFillKeyLibraryDialogSource, 'manage'>;
    skippedRowCount: number;
    duplicateRowCount: number;
  }
  | ChangeEntryAction
  | { type: 'delete-entry'; index: number }
  | { type: 'add-entry' }
  | { type: 'toggle-selection'; index: number; selected: boolean }
  | { type: 'set-visible-selection'; indexes: readonly number[]; selected: boolean }
  | { type: 'delete-selected' }
  | { type: 'confirm-conflict'; id: string; confirmed: boolean }
  | { type: 'request-close' }
  | { type: 'request-import'; file: File }
  | { type: 'request-delete-selected' }
  | { type: 'cancel-guard' }
  | { type: 'discard-close' }
  | { type: 'start-import' }
  | { type: 'import-success'; entries: readonly LearnedAutoFillPatternV1[] }
  | { type: 'import-error'; error: string }
  | { type: 'revert-local-edits' }
  | { type: 'save-local-success'; entries: readonly LearnedAutoFillPatternV1[]; notice: string }
  | { type: 'persist-success'; entries: readonly LearnedAutoFillPatternV1[]; notice: string }
  | { type: 'set-error'; error?: string }
  | { type: 'set-notice'; notice?: string };

const cloneEntries = (
  entries: readonly LearnedAutoFillPatternV1[]
): LearnedAutoFillPatternV1[] => [...entries];

export function createQuickFillKeyLibraryEditorState(
  entries: readonly LearnedAutoFillPatternV1[]
): QuickFillKeyLibraryEditorState {
  return {
    open: false,
    source: 'manage',
    persistedEntries: cloneEntries(entries),
    libraryEntries: cloneEntries(entries),
    drafts: [],
    selectedLocalEntryIndexes: new Set(),
    confirmedConflictIds: new Set(),
    skippedRowCount: 0,
    duplicateRowCount: 0,
  };
}

export const getQuickFillKeyLibraryIsDirty = (
  state: QuickFillKeyLibraryEditorState
): boolean => JSON.stringify(state.libraryEntries) !== JSON.stringify(state.persistedEntries);

export const getQuickFillKeyLibraryHasDiscardableWork = (
  state: QuickFillKeyLibraryEditorState
): boolean => getQuickFillKeyLibraryIsDirty(state) || state.drafts.length > 0;

const resetTransient = () => ({
  selectedLocalEntryIndexes: new Set<number>(),
  confirmedConflictIds: new Set<string>(),
  error: undefined,
  notice: undefined,
  guardIntent: undefined,
});

function setSelection(
  current: ReadonlySet<number>,
  indexes: readonly number[],
  selected: boolean
): Set<number> {
  const next = new Set(current);
  indexes.forEach((index) => selected ? next.add(index) : next.delete(index));
  return next;
}

export function quickFillKeyLibraryEditorReducer(
  state: QuickFillKeyLibraryEditorState,
  action: QuickFillKeyLibraryEditorAction
): QuickFillKeyLibraryEditorState {
  switch (action.type) {
    case 'open-manage':
      return {
        ...createQuickFillKeyLibraryEditorState(action.entries),
        open: true,
      };
    case 'open-preview':
      return {
        ...createQuickFillKeyLibraryEditorState(action.entries),
        open: true,
        source: action.source,
        drafts: cloneEntries(action.drafts),
        skippedRowCount: action.skippedRowCount,
        duplicateRowCount: action.duplicateRowCount,
      };
    case 'change-library-entry':
      return {
        ...state,
        libraryEntries: updateQuickFillKeyLibraryEntry(
          state.libraryEntries, action.index, action.field, action.value
        ),
        notice: undefined,
      };
    case 'change-draft-entry':
      return {
        ...state,
        drafts: updateQuickFillKeyLibraryEntry(
          state.drafts, action.index, action.field, action.value
        ),
        confirmedConflictIds: new Set(),
        notice: undefined,
      };
    case 'delete-entry':
      return {
        ...state,
        libraryEntries: state.libraryEntries.filter((_entry, index) => index !== action.index),
        selectedLocalEntryIndexes: new Set(),
        notice: undefined,
      };
    case 'add-entry':
      return {
        ...state,
        libraryEntries: [createEmptyQuickFillKeyLibraryEntry(), ...state.libraryEntries],
        selectedLocalEntryIndexes: new Set(),
        notice: undefined,
      };
    case 'toggle-selection':
      return {
        ...state,
        selectedLocalEntryIndexes: setSelection(
          state.selectedLocalEntryIndexes, [action.index], action.selected
        ),
      };
    case 'set-visible-selection':
      return {
        ...state,
        selectedLocalEntryIndexes: setSelection(
          state.selectedLocalEntryIndexes, action.indexes, action.selected
        ),
      };
    case 'delete-selected':
      return {
        ...state,
        libraryEntries: state.libraryEntries.filter(
          (_entry, index) => !state.selectedLocalEntryIndexes.has(index)
        ),
        selectedLocalEntryIndexes: new Set(),
        guardIntent: undefined,
        notice: undefined,
      };
    case 'confirm-conflict': {
      const confirmedConflictIds = new Set(state.confirmedConflictIds);
      action.confirmed ? confirmedConflictIds.add(action.id) : confirmedConflictIds.delete(action.id);
      return { ...state, confirmedConflictIds };
    }
    case 'request-close':
      return getQuickFillKeyLibraryHasDiscardableWork(state)
        ? { ...state, guardIntent: { type: 'close' } }
        : { ...state, open: false, selectedLocalEntryIndexes: new Set() };
    case 'request-import':
      return getQuickFillKeyLibraryHasDiscardableWork(state)
        ? { ...state, guardIntent: { type: 'import', file: action.file } }
        : { ...state, guardIntent: { type: 'import', file: action.file } };
    case 'request-delete-selected':
      return state.selectedLocalEntryIndexes.size > 0
        ? { ...state, guardIntent: { type: 'delete-selected' } }
        : state;
    case 'cancel-guard':
      return { ...state, guardIntent: undefined };
    case 'discard-close':
      return {
        ...createQuickFillKeyLibraryEditorState(state.persistedEntries),
        open: false,
      };
    case 'start-import':
      return {
        ...state,
        open: true,
        source: 'import',
        libraryEntries: cloneEntries(state.persistedEntries),
        drafts: [],
        skippedRowCount: 0,
        duplicateRowCount: 0,
        ...resetTransient(),
      };
    case 'import-success':
      return { ...state, drafts: cloneEntries(action.entries), error: undefined };
    case 'import-error':
      return { ...state, drafts: [], error: action.error };
    case 'revert-local-edits':
      return {
        ...state,
        libraryEntries: cloneEntries(state.persistedEntries),
        selectedLocalEntryIndexes: new Set(),
        notice: undefined,
      };
    case 'save-local-success':
      return {
        ...state,
        persistedEntries: cloneEntries(action.entries),
        libraryEntries: cloneEntries(action.entries),
        selectedLocalEntryIndexes: new Set(),
        confirmedConflictIds: new Set(),
        error: undefined,
        notice: action.notice,
        guardIntent: undefined,
      };
    case 'persist-success':
      return {
        ...state,
        persistedEntries: cloneEntries(action.entries),
        libraryEntries: cloneEntries(action.entries),
        drafts: [],
        selectedLocalEntryIndexes: new Set(),
        confirmedConflictIds: new Set(),
        source: 'manage',
        error: undefined,
        notice: action.notice,
      };
    case 'set-error':
      return { ...state, error: action.error };
    case 'set-notice':
      return { ...state, notice: action.notice };
    default:
      return state;
  }
}
